/**
 * ServerDirectoryPanel — statusbalk boven de gebruikerstabel.
 *
 * WAT DIT NIET MEER IS
 *
 * Eerder was dit een tweede lijst naast de tabel. Dat was de verkeerde plek:
 * de beheerder zag serveraccounts pas nadat hij ergens anders op klikte,
 * terwijl de tabel — de plek waar hij kijkt — leeg bleef. De accounts staan nu
 * gewoon ín de tabel (zie `useUsersData`), en wat hier overblijft is de
 * herkomst: hoeveel rijen komen van de server, wanneer is dat opgehaald, en
 * lukte het.
 *
 * Die statusregel is wél nodig. Een tabel die stilzwijgend twee bronnen mengt,
 * is niet te vertrouwen op het moment dat er iets ontbreekt: je weet dan niet
 * of een account niet bestaat of dat de verbinding faalde.
 */

import React, { useMemo } from 'react';
import { Database, RefreshCw, AlertTriangle, ShieldAlert } from 'lucide-react';
import type { ServerDirectoryState } from './useServerDirectory';
interface ServerDirectoryPanelProps {
  directory: ServerDirectoryState;
  /** Zet het rolfilter op 'teacher' zodat de wachtenden vindbaar zijn. */
  onShowTeachers?: () => void;
}
export function ServerDirectoryPanel({
  directory,
  onShowTeachers
}: ServerDirectoryPanelProps) {
  const {
    accounts,
    loading,
    error,
    loaded,
    refresh
  } = directory;

  /**
   * Leerkrachten die op verificatie wachten.
   *
   * Dit is de enige statistiek uit deze lijst die om ACTIE vraagt, en tot nu toe
   * was hij nergens te zien: `teacher_status` kwam wel over de lijn maar werd
   * door geen scherm gelezen. Een leerkracht met `pending` kan inloggen en komt
   * bij geen enkele leerling — dat is een wachtrij die alleen leegloopt als
   * iemand hem opmerkt.
   */
  const pendingTeachers = useMemo(() => accounts.filter(a => a.role === 'teacher' && a.teacherStatus === 'pending').length, [accounts]);
  return <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5" data-id="element-7850" data-mp="3e8p2qoewov3m">
      <p className="flex items-center gap-2 text-sm text-slate-700" data-id="element-7851" data-mp="3j19wxr1q5a7o">
        {error ? <>
            <AlertTriangle className="w-4 h-4 text-amber-600" data-id="element-7852" data-mp="3sv5okw57nyxr" />
            <span className="font-semibold text-amber-800" data-id="element-7853" data-mp="3nw2q5ujqkqyr">{error}</span>
          </> : <>
            <Database className="w-4 h-4 text-slate-500" data-id="element-7854" data-mp="3iwyk3cs3z9cn" />
            {loaded ? <span data-id="element-7855" data-mp="3izcd7g38abhk">
                <strong className="font-semibold text-slate-900" data-id="element-7856" data-mp="3ew68wdzoeudq">
                  {accounts.length}
                </strong>{' '}
                account{accounts.length === 1 ? '' : 'en'} van de server.
                Namen van leerlingen zijn afgeschermd tot je een
                toegangsvenster opent.
              </span> : <span data-id="element-7857" data-mp="3068irsi6rulw">Accounts van de server ophalen…</span>}
          </>}
      </p>

      <div className="flex items-center gap-2" data-id="element-7858" data-mp="2v86xr2179l7s">
        {/*
          Alleen tonen als er iets te doen is. Een teller die meestal "0" zegt,
          wordt niet meer gelezen op het moment dat er wél iemand wacht.
         */}
        {pendingTeachers > 0 && <button onClick={onShowTeachers} className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs font-bold text-amber-900 transition-colors hover:bg-amber-100" data-id="element-7859" data-mp="2m7qwlz7xiq63">
            <ShieldAlert className="w-3.5 h-3.5" data-id="element-7860" data-mp="16qduugzmdq4u" />
            {pendingTeachers} leerkracht{pendingTeachers === 1 ? '' : 'en'} wacht
            {pendingTeachers === 1 ? '' : 'en'} op verificatie
          </button>}

        <button onClick={() => void refresh()} disabled={loading} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50" data-id="element-7861" data-mp="iyoympjoghim">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} data-id="element-7862" data-mp="ryby35sujd91" />
          Vernieuwen
        </button>
      </div>
    </div>;
}